import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { urlMidiaManutencao } from '../lib/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Lock, FileText, RefreshCw, Download, X, ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';

const FOTO_TIPO_LABEL = {
  antes: 'Antes',
  depois: 'Depois',
  problema: 'Problema',
  geral: 'Geral',
};

const isVideo = (foto) => {
  const mime = String(foto?.mime_type || '').toLowerCase();
  if (mime.startsWith('video')) return true;
  return /\.(mp4|mov|m4v|3gp|webm)(\?|$)/i.test(foto?.url || '');
};

const toDataUri = (b64, mime = 'image/jpeg') => {
  if (!b64) return null;
  if (b64.startsWith('data:')) return b64;
  return `data:${mime};base64,${b64}`;
};

const uriThumb = (foto) => {
  if (isVideo(foto)) return toDataUri(foto?.base64_thumb) || null;
  return toDataUri(foto?.base64_thumb) || toDataUri(foto?.base64) || (foto?.url || null);
};

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

const uriOriginal = (foto, manutencaoId) => {
  if (isVideo(foto)) {
    // Sempre o stream da API. file:// do celular e data URI grande não reproduzem.
    const stream = urlMidiaManutencao(manutencaoId, foto?.id);
    if (stream) return stream;
    if (isHttpUrl(foto?.url)) return foto.url;
    if (foto?.base64) return toDataUri(foto.base64, foto.mime_type || 'video/mp4');
    return null;
  }
  return toDataUri(foto?.base64) || toDataUri(foto?.base64_thumb) || (isHttpUrl(foto?.url) ? foto.url : null);
};

const RelatorioManutencaoDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [manutencao, setManutencao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [reenviandoM8, setReenviandoM8] = useState(false);
  const [fotoAbertaIndex, setFotoAbertaIndex] = useState(null);
  const [videoErro, setVideoErro] = useState(false);
  const [complementos, setComplementos] = useState([]);
  const [form, setForm] = useState({
    descricao_servico: '',
    problemas_encontrados: '',
    acoes_realizadas: '',
    recomendacoes: ''
  });

  const assinada = manutencao?.assinatura != null && Object.keys(manutencao.assinatura || {}).length > 0;

  const fotos = useMemo(() => {
    return (manutencao?.fotos || []).filter((f) => isVideo(f) || uriThumb(f) || uriOriginal(f, id));
  }, [manutencao, id]);

  useEffect(() => {
    loadManutencao();
  }, [id]);

  const fecharFoto = useCallback(() => {
    setFotoAbertaIndex(null);
    setVideoErro(false);
  }, []);

  const navegarFoto = useCallback(
    (delta) => {
      setFotoAbertaIndex((atual) => {
        if (atual === null) return null;
        const next = atual + delta;
        if (next < 0 || next >= fotos.length) return atual;
        return next;
      });
    },
    [fotos.length]
  );

  useEffect(() => {
    if (fotoAbertaIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') fecharFoto();
      if (e.key === 'ArrowLeft') navegarFoto(-1);
      if (e.key === 'ArrowRight') navegarFoto(1);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fotoAbertaIndex, fecharFoto, navegarFoto]);

  useEffect(() => {
    setVideoErro(false);
  }, [fotoAbertaIndex]);

  const loadManutencao = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/manutencoes/${id}`);
      setManutencao(response.data);
      setForm({
        descricao_servico: response.data.descricao_servico || '',
        problemas_encontrados: response.data.problemas_encontrados || '',
        acoes_realizadas: response.data.acoes_realizadas || '',
        recomendacoes: response.data.recomendacoes || ''
      });
      try {
        const compRes = await api.get('/complementos-materiais', { params: { manutencao_id: id, limit: 50 } });
        setComplementos(compRes.data?.complementos || []);
      } catch {
        setComplementos([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Relatório não encontrado');
      navigate('/relatorios-manutencao');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (assinada) return;
    setSaving(true);
    try {
      await api.put(`/manutencoes/${id}`, form);
      toast.success('Alterações salvas.');
      setManutencao((prev) => ({ ...prev, ...form }));
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao salvar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (str) => {
    if (!str) return '—';
    try {
      const d = new Date(str);
      return d.toLocaleString('pt-BR');
    } catch {
      return str;
    }
  };

  const statusLabel = (s) => {
    const map = { em_andamento: 'Em andamento', concluida: 'Concluída', pendente_assinatura: 'Pendente assinatura', cancelada: 'Cancelada' };
    return map[s] || s;
  };

  const tipoLabel = (t) => {
    const map = { preventiva: 'Preventiva', corretiva: 'Corretiva', nao_autorizado: 'Não autorizado', emergencia: 'Emergência', instalacao: 'Instalação', inspecao: 'Inspeção', retirar_equipamento: 'Retirar Equipamento', visita_tecnica: 'Visita Técnica' };
    return map[t] || t;
  };

  const m8Label = (m) => {
    if (m.m8_ordem_servico_id) return `OS ${m.m8_ordem_servico_id}`;
    const s = m.m8_sync_status;
    if (s === 'synced') return 'Sincronizado';
    if (s === 'pending') return 'Pendente';
    if (s === 'processing') return 'Processando';
    if (s === 'failed') return 'Falhou';
    if (s === 'not_mapped') return 'Não mapeado';
    if (s === 'max_retries') return 'Máx. tentativas';
    return '—';
  };

  const jaSincronizadoM8 = () => {
    if (!manutencao) return true;
    if (manutencao.m8_ordem_servico_id) return true;
    return manutencao.m8_sync_status === 'synced';
  };

  const podeReenviarM8 = () => {
    if (!manutencao) return false;
    if (!assinada || manutencao.status !== 'concluida') return false;
    return !jaSincronizadoM8();
  };

  const reenviarM8 = async () => {
    if (!window.confirm('Tem certeza que quer sincronizar? Pode gerar Duplicado.')) return;
    setReenviandoM8(true);
    try {
      await api.post(`/manutencoes/${id}/m8/reenviar`);
      toast.success('Integração M8 reenfileirada');
      loadManutencao();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reenviar integração M8');
    } finally {
      setReenviandoM8(false);
    }
  };

  const baixarPdf = async () => {
    if (!id || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/manutencoes/${id}/pdf`, {
        responseType: 'blob',
        params: { t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const numero = manutencao?.numero_relatorio || manutencao?.id || id;
      a.download = `relatorio_manutencao_${numero}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download do PDF iniciado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao baixar PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading || !manutencao) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/relatorios-manutencao"
            className="p-2 rounded-xl hover:bg-slate-100 text-slate-600"
            title="Voltar"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Relatório de Manutenção</h1>
              <p className="text-slate-600 text-sm">
                {manutencao.cliente_nome} · {tipoLabel(manutencao.tipo_servico)}
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={baixarPdf}
          disabled={downloadingPdf}
          className="flex items-center gap-2 bg-white text-slate-700 px-4 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
        >
          <Download className="w-5 h-5" />
          {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
        </button>
        {assinada ? (
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 text-amber-800 border border-amber-200">
            <Lock className="w-5 h-5" />
            <span className="font-medium">Assinado — somente visualização</span>
          </div>
        ) : (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-5 py-2.5 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Dados do atendimento</h2>
          <dl className="space-y-3 text-sm">
            <div><dt className="text-slate-500">Cliente</dt><dd className="font-medium text-slate-900">{manutencao.cliente_nome || '—'}</dd></div>
            {manutencao.cliente_codigo && (
              <div><dt className="text-slate-500">Código (M8)</dt><dd className="font-medium text-slate-900">{manutencao.cliente_codigo}</dd></div>
            )}
            <div><dt className="text-slate-500">Técnico</dt><dd className="font-medium text-slate-900">{manutencao.tecnico_nome || '—'}</dd></div>
            <div><dt className="text-slate-500">Carro</dt><dd className="font-medium text-slate-900">{manutencao.carro_placa || '—'}</dd></div>
            <div><dt className="text-slate-500">Tipo de serviço</dt><dd className="font-medium text-slate-900">{tipoLabel(manutencao.tipo_servico)}</dd></div>
            {manutencao.m8_equipamento_nome && (
              <div><dt className="text-slate-500">Equipamento M8</dt><dd className="font-medium text-slate-900">{manutencao.m8_equipamento_nome}</dd></div>
            )}
            {manutencao.equipamento_numero_serie && (
              <div><dt className="text-slate-500">Nº de série</dt><dd className="font-medium text-slate-900">{manutencao.equipamento_numero_serie}</dd></div>
            )}
            <div><dt className="text-slate-500">Status</dt><dd className="font-medium text-slate-900">{statusLabel(manutencao.status)}</dd></div>
            <div><dt className="text-slate-500">Criado em</dt><dd className="font-medium text-slate-900">{formatDate(manutencao.created_at)}</dd></div>
            {manutencao.hora_chegada && <div><dt className="text-slate-500">Chegada</dt><dd className="font-medium text-slate-900">{formatDate(manutencao.hora_chegada)}</dd></div>}
            {manutencao.hora_saida && <div><dt className="text-slate-500">Saída</dt><dd className="font-medium text-slate-900">{formatDate(manutencao.hora_saida)}</dd></div>}
          </dl>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Integração M8</h2>
          <dl className="space-y-3 text-sm">
            <div><dt className="text-slate-500">Status M8</dt><dd className="font-medium text-slate-900">{m8Label(manutencao)}</dd></div>
            <div><dt className="text-slate-500">OS M8</dt><dd className="font-medium text-slate-900">{manutencao.m8_ordem_servico_id || '—'}</dd></div>
            <div><dt className="text-slate-500">Requisição M8</dt><dd className="font-medium text-slate-900">{manutencao.m8_requisicao_id || '—'}</dd></div>
            <div><dt className="text-slate-500">Número relatório</dt><dd className="font-medium text-slate-900">{manutencao.numero_relatorio || '—'}</dd></div>
            <div>
              <dt className="text-slate-500">Erro integração</dt>
              <dd className="font-medium text-rose-700 whitespace-pre-wrap break-words">{manutencao.m8_sync_error || '—'}</dd>
            </div>
          </dl>
          <div className="pt-4">
            <button
              type="button"
              onClick={reenviarM8}
              disabled={!podeReenviarM8() || reenviandoM8}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${reenviandoM8 ? 'animate-spin' : ''}`} />
              {reenviandoM8 ? 'Sincronizando...' : 'Reenviar integração M8'}
            </button>
            {jaSincronizadoM8() && (
              <p className="mt-2 text-xs text-slate-500">Já sincronizado com o M8 — reenvio desabilitado.</p>
            )}
          </div>
        </div>

        {manutencao.pesquisa_resposta && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Pesquisa de atendimento</h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-slate-500">Quem preencheu</dt>
                <dd className="font-medium text-slate-900">{manutencao.assinatura?.nome || '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Qualidade do serviço</dt>
                <dd className="font-medium text-slate-900">{manutencao.pesquisa_resposta.nota_servico ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Técnico</dt>
                <dd className="font-medium text-slate-900">{manutencao.pesquisa_resposta.nota_tecnico ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Equipamento</dt>
                <dd className="font-medium text-slate-900">
                  {manutencao.pesquisa_resposta.equipamento_nao_avaliado
                    ? 'Não avaliou'
                    : (manutencao.pesquisa_resposta.nota_equipamento ?? '—')}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Comentário</dt>
                <dd className="font-medium text-slate-900 whitespace-pre-wrap">{manutencao.pesquisa_resposta.comentario || '—'}</dd>
              </div>
              {manutencao.pesquisa_resposta.respondido_em && (
                <div>
                  <dt className="text-slate-500">Respondida em</dt>
                  <dd className="font-medium text-slate-900">{formatDate(manutencao.pesquisa_resposta.respondido_em)}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {manutencao.assinatura && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Assinatura</h2>
            {manutencao.assinatura.data_hora && (
              <p className="text-sm text-slate-600 mb-3">Assinado em {formatDate(manutencao.assinatura.data_hora)}</p>
            )}
            {(manutencao.assinatura.base64 || manutencao.assinatura.imagem_base64) && (
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 inline-block">
                <img
                  src={toDataUri(manutencao.assinatura.base64 || manutencao.assinatura.imagem_base64)}
                  alt="Assinatura"
                  className="max-h-24 object-contain"
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-bold text-slate-900">Fotos e vídeos</h2>
          <span className="text-sm text-slate-500">
            {fotos.length} item{fotos.length !== 1 ? 's' : ''}
          </span>
        </div>
        {fotos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {fotos.map((foto, index) => {
              const src = uriThumb(foto);
              const video = isVideo(foto);
              return (
                <button
                  key={foto.id || index}
                  type="button"
                  onClick={() => setFotoAbertaIndex(index)}
                  className="group relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  title={video ? 'Reproduzir vídeo' : 'Ampliar foto'}
                >
                  {src && !video ? (
                    <img
                      src={src}
                      alt={foto.descricao || FOTO_TIPO_LABEL[foto.tipo] || 'Foto'}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center bg-slate-900 text-white">
                      <span className="text-2xl font-bold">▶</span>
                      <span className="text-xs mt-1 font-semibold">Vídeo</span>
                    </div>
                  )}
                  <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-xs px-2 py-1 truncate">
                    {video ? 'Vídeo · ' : ''}
                    {FOTO_TIPO_LABEL[foto.tipo] || foto.tipo || 'Mídia'}
                    {foto.descricao ? ` · ${foto.descricao}` : ''}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-3 text-slate-500 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 justify-center">
            <ImageIcon className="w-5 h-5" />
            <span className="text-sm">Nenhuma foto ou vídeo registrado neste relatório.</span>
          </div>
        )}
      </div>

      {(manutencao.pecas || []).length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Materiais utilizados</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500">
                  <th className="py-2 pr-3 font-medium">Código</th>
                  <th className="py-2 pr-3 font-medium">Descrição</th>
                  <th className="py-2 font-medium text-right">Qtd</th>
                </tr>
              </thead>
              <tbody>
                {(manutencao.pecas || []).map((peca, idx) => (
                  <tr key={peca.peca_id || peca.codigo || idx} className="border-b border-slate-100">
                    <td className="py-2.5 pr-3 text-slate-900 font-medium">{peca.codigo || '—'}</td>
                    <td className="py-2.5 pr-3 text-slate-900">{peca.nome || '—'}</td>
                    <td className="py-2.5 text-right text-slate-900">{peca.quantidade ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {complementos.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-sky-100">
          <h2 className="text-lg font-bold text-slate-900 mb-1">Materiais complementares</h2>
          <p className="text-sm text-slate-500 mb-4">Não constam no relatório original — só acertam o estoque.</p>
          {complementos.map((c) => (
            <div key={c.id} className="mb-4 last:mb-0 rounded-xl border border-slate-100 p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-medium text-slate-900">{c.numero_documento || c.id}</span>
                <span className="text-xs text-slate-500">{c.tecnico_nome} · {c.cargo || 'VIAFILTROS'}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {(c.pecas || []).map((peca, idx) => (
                    <tr key={peca.peca_id || idx} className="border-b border-slate-50">
                      <td className="py-1.5 pr-3 text-slate-700">{peca.codigo || '—'}</td>
                      <td className="py-1.5 pr-3 text-slate-900">{peca.nome || '—'}</td>
                      <td className="py-1.5 text-right text-slate-900">{peca.quantidade ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Medições — Condutividade</h2>
        {manutencao.condutividade_nao_se_aplica ? (
          <p className="text-sm text-slate-600">Não se aplica a este atendimento.</p>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-slate-500">Condutividade Membrana</dt>
              <dd className="font-medium text-slate-900 text-lg">
                {manutencao.condutividade_membrana_valor != null
                  ? `${Number(manutencao.condutividade_membrana_valor).toFixed(2)} ${manutencao.condutividade_unidade === 'mohm_cm' ? 'MΩ·cm' : 'µS/cm'}`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Condutividade Final</dt>
              <dd className="font-medium text-slate-900 text-lg">
                {(manutencao.condutividade_final_valor ?? manutencao.condutividade_valor) != null
                  ? `${Number(manutencao.condutividade_final_valor ?? manutencao.condutividade_valor).toFixed(2)} ${manutencao.condutividade_unidade === 'mohm_cm' ? 'MΩ·cm' : 'µS/cm'}`
                  : '—'}
              </dd>
            </div>
          </dl>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        <h2 className="text-lg font-bold text-slate-900">Descrição e observações</h2>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Descrição do serviço</label>
          {assinada ? (
            <p className="text-slate-900 whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 min-h-[80px]">{manutencao.descricao_servico || '—'}</p>
          ) : (
            <textarea
              value={form.descricao_servico}
              onChange={(e) => setForm({ ...form, descricao_servico: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-y"
              placeholder="Descreva o serviço realizado"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Problemas encontrados</label>
          {assinada ? (
            <p className="text-slate-900 whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 min-h-[80px]">{manutencao.problemas_encontrados || '—'}</p>
          ) : (
            <textarea
              value={form.problemas_encontrados}
              onChange={(e) => setForm({ ...form, problemas_encontrados: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-y"
              placeholder="Problemas identificados"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Ações realizadas</label>
          {assinada ? (
            <p className="text-slate-900 whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 min-h-[80px]">{manutencao.acoes_realizadas || '—'}</p>
          ) : (
            <textarea
              value={form.acoes_realizadas}
              onChange={(e) => setForm({ ...form, acoes_realizadas: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-y"
              placeholder="O que foi feito"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Recomendações</label>
          {assinada ? (
            <p className="text-slate-900 whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 min-h-[80px]">{manutencao.recomendacoes || '—'}</p>
          ) : (
            <textarea
              value={form.recomendacoes}
              onChange={(e) => setForm({ ...form, recomendacoes: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-y"
              placeholder="Recomendações"
            />
          )}
        </div>
      </div>

      {fotoAbertaIndex !== null && fotos[fotoAbertaIndex] && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/90"
          role="dialog"
          aria-modal="true"
          aria-label="Visualização de foto"
          onClick={fecharFoto}
        >
          <div className="flex items-center justify-between px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
            <span className="text-sm font-medium">
              {fotoAbertaIndex + 1} / {fotos.length}
              {' · '}
              {FOTO_TIPO_LABEL[fotos[fotoAbertaIndex].tipo] || fotos[fotoAbertaIndex].tipo || 'Foto'}
              {fotos[fotoAbertaIndex].descricao ? ` · ${fotos[fotoAbertaIndex].descricao}` : ''}
            </span>
            <button
              type="button"
              onClick={fecharFoto}
              className="p-2 rounded-lg hover:bg-white/10"
              aria-label="Fechar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center px-4 pb-4 min-h-0" onClick={(e) => e.stopPropagation()}>
            {fotos.length > 1 && (
              <button
                type="button"
                onClick={() => navegarFoto(-1)}
                disabled={fotoAbertaIndex === 0}
                className="absolute left-3 md:left-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Foto anterior"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
            )}
            {isVideo(fotos[fotoAbertaIndex]) ? (
              uriOriginal(fotos[fotoAbertaIndex], id) && !videoErro ? (
                <div className="flex flex-col items-center gap-3 max-w-full">
                  <video
                    key={fotos[fotoAbertaIndex].id || fotoAbertaIndex}
                    src={`${uriOriginal(fotos[fotoAbertaIndex], id)}#t=0.1`}
                    controls
                    playsInline
                    preload="auto"
                    className="max-h-[80vh] w-auto max-w-full rounded-lg bg-black"
                    onError={() => setVideoErro(true)}
                  />
                  <a
                    href={uriOriginal(fotos[fotoAbertaIndex], id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-white/80 underline"
                  >
                    Abrir vídeo em nova aba
                  </a>
                </div>
              ) : (
                <div className="text-white text-center px-6 max-w-md">
                  <p className="text-lg font-semibold">Vídeo indisponível</p>
                  <p className="text-sm text-white/70 mt-2">
                    O arquivo não chegou ao servidor ou o formato não reproduz neste navegador.
                  </p>
                  {uriOriginal(fotos[fotoAbertaIndex], id) ? (
                    <a
                      href={uriOriginal(fotos[fotoAbertaIndex], id)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-3 text-sm text-emerald-300 underline"
                    >
                      Tentar abrir o arquivo
                    </a>
                  ) : null}
                </div>
              )
            ) : (
              <img
                src={uriOriginal(fotos[fotoAbertaIndex], id)}
                alt={fotos[fotoAbertaIndex].descricao || 'Foto da manutenção'}
                className="max-h-full max-w-full object-contain rounded-lg"
              />
            )}
            {fotos.length > 1 && (
              <button
                type="button"
                onClick={() => navegarFoto(1)}
                disabled={fotoAbertaIndex === fotos.length - 1}
                className="absolute right-3 md:right-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Próxima foto"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatorioManutencaoDetalhe;
